import { useEffect, useMemo, useRef, useState, Dispatch } from 'react';
import './index.scss';

import { UbiverseClient, UbiverseEventTopicList, UbiverseNodeType__Child } from '../../types';
import { RealtimeClient } from '../../services/realtime-client';
import { IGlobalState, GlobalAction } from '../../states/GlobalState';
import NavigationBar, { NavigationSection } from '../../components/NavigationBar';
import AuthDialog from '../../components/AuthDialog';
import { useAuth } from '../../auth/AuthProvider';

interface UbiSimProps {
  realtimeClient: RealtimeClient;
  state: IGlobalState;
  dispatch: Dispatch<GlobalAction>;
}

const UbiSim = ({ realtimeClient }: UbiSimProps) => {
  const { isAuthenticated, isConfigured, signOut, user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [simName, setSimName] = useState('');
  const [simState, setSimState] = useState<UbiverseClient>({
    id: '',
    type: UbiverseNodeType__Child,
    data: { label: '', intensity: 0 }
  });
  const [intensity] = useState(0);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  // Device emulation: announce this session on the presence roster as
  // kind:'device' with a hardware_id, exactly as the ESP32 firmware does, so
  // the Devices page's Online status and the SimulatorNode's device entries
  // can be exercised without hardware. Enter the hardware_id of a claimed
  // device row to light it up on /devices.
  const [emulateDevice, setEmulateDevice] = useState(false);
  const [hardwareId, setHardwareId] = useState('');
  const [revokedNotice, setRevokedNotice] = useState(false);
  // Refs mirror emulation state for the stable event handlers below.
  const emulateRef = useRef(false);
  const hardwareIdRef = useRef('');

  const requestLogin = () => {
    setIsAuthDialogOpen(true);
  };

  // Acks identify their sender so the dashboard can address the follow-up
  // Set* (per-device play). The sim is addressable by its connection id —
  // RealtimeClient filters inbound targetIds against it — where the firmware
  // uses its hardware_id; both go out as `clientId`.
  const ackFrom = () => ({ clientId: realtimeClient.getConnectionId() });

  const emitBufferUpdatedHandler = () => {
    if (!isAuthenticated) {
      requestLogin();
      return;
    }

    realtimeClient.emitEvent(UbiverseEventTopicList.AckBufferUpdated, ackFrom());
  };

  useEffect(() => {
    realtimeClient.subscribeEvent(UbiverseEventTopicList.SetData, (payload) => {
      const { data } = payload;
      console.log('UbiSim:: Set Data', data);
      if (data) {
        realtimeClient.emitEvent(UbiverseEventTopicList.AckDataChanged, ackFrom());
      }
    });

    realtimeClient.subscribeEvent(UbiverseEventTopicList.SetPlay, (payload) => {
      const { isPlaying } = payload;
      console.log('UbiSim:: Set Play', isPlaying)
      setIsPlaying(isPlaying);
      realtimeClient.emitEvent(UbiverseEventTopicList.AckPlayChanged, { isPlaying, ...ackFrom() });
    })

    // Mirrors firmware ubResolver.ino: optional nodeId targeting — a nodeId
    // addressed to neither our hardware_id nor our connection id is ignored.
    realtimeClient.subscribeEvent(UbiverseEventTopicList.SetMute, (payload) => {
      const { isMute, nodeId } = payload || {};
      if (nodeId && nodeId !== hardwareIdRef.current && nodeId !== realtimeClient.getConnectionId()) {
        return;
      }
      console.log('UbiSim:: Set Mute', isMute);
      setIsMuted(Boolean(isMute));
      realtimeClient.emitEvent(UbiverseEventTopicList.AckMuteChanged,
        nodeId ? { isMute: Boolean(isMute), nodeId, ...ackFrom() } : { isMute: Boolean(isMute), ...ackFrom() });
    });

    realtimeClient.subscribeEvent(UbiverseEventTopicList.ClearData, () => {
      console.log('UbiSim:: Clear Data');
      setIsPlaying(false);
    });

    // Mirrors the firmware's revoke path: on a revoke broadcast for our
    // hardware_id the ESP32 verifies with /device-status and wipes its EEPROM;
    // the emulator drops its device identity and reports it.
    realtimeClient.subscribeEvent(UbiverseEventTopicList.DeviceRevoked, (payload) => {
      if (!emulateRef.current) return;
      if (!payload?.hardware_id || payload.hardware_id !== hardwareIdRef.current) return;
      console.log('UbiSim:: Device revoked — dropping device identity');
      setEmulateDevice(false);
      setRevokedNotice(true);
    });

    return () => {
      realtimeClient.unsubscribeEvent(UbiverseEventTopicList.SetData);
      realtimeClient.unsubscribeEvent(UbiverseEventTopicList.SetPlay);
      realtimeClient.unsubscribeEvent(UbiverseEventTopicList.SetMute);
      realtimeClient.unsubscribeEvent(UbiverseEventTopicList.ClearData);
      realtimeClient.unsubscribeEvent(UbiverseEventTopicList.DeviceRevoked);
    };
  }, [realtimeClient]);

  // Push the chosen identity onto the presence roster whenever it changes.
  useEffect(() => {
    emulateRef.current = emulateDevice && Boolean(hardwareId.trim());
    hardwareIdRef.current = hardwareId.trim();
    if (emulateDevice && hardwareId.trim()) {
      realtimeClient.setPresenceIdentity('device', hardwareId.trim());
    } else {
      realtimeClient.setPresenceIdentity('simulator', null);
    }
  }, [emulateDevice, hardwareId, isAuthenticated, realtimeClient]);

  useEffect(() => {
    console.log('UbiSim:: Intensity is set', intensity);
    setSimState({ ...simState, data: { ...simState.data, intensity } });
  }, [intensity]);

  useEffect(() => {
    setSimState( { ...simState, data: { ...simState.data, label: simName } })
    realtimeClient.updatePresenceData({ label: simName, intensity });
  }, [simName]);

  useEffect(() => {
    console.log('UbiSim:: Update sim state - ', simState);
  }, [simState]);

  useEffect(() => {
    setSimState((prev) => ({ ...prev, id: realtimeClient.getConnectionId() }));
    setHardwareId((prev) => prev || `sim-${realtimeClient.getConnectionId().slice(0, 8)}`);
  }, [realtimeClient]);

  const navigationSections: NavigationSection[] = useMemo(() => {
    const userLabel = user?.email || user?.user_metadata?.display_name || '(not signed in)';

    return [
      {
        label: 'Simulator',
        actions: [
          { label: 'Dashboard', onClick: () => window.location.assign('/') },
          { label: `Endpoint: ${simName || 'unnamed simulator'}`, onClick: () => undefined, disabled: true }
        ]
      },
      {
        label: 'Account',
        actions: [
          { label: `Status: ${isAuthenticated ? 'Authenticated' : 'Guest'}`, onClick: () => undefined, disabled: true },
          { label: `User: ${userLabel}`, onClick: () => undefined, disabled: true },
          { label: 'Log in', onClick: requestLogin, disabled: isAuthenticated || !isConfigured },
          { label: 'Log out', onClick: signOut, disabled: !isAuthenticated }
        ],
        ...(isAuthenticated ? { avatar: { name: user?.user_metadata?.display_name || user?.email || undefined } } : {}),
      }
    ];
  }, [isAuthenticated, isConfigured, signOut, simName, user]);

  return (
    <div className="page--ubi-sim">
      <NavigationBar sections={navigationSections} productName="Ubiquitel Simulator" />
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onClose={() => setIsAuthDialogOpen(false)}
        title="Simulator account"
      />
      <div className="page--ubi-sim__container">
        <div className="page--ubi-sim__header">
          <div>
            <p className="page--ubi-sim__eyebrow">Simulator endpoint</p>
            <h2>{isAuthenticated ? 'Authenticated simulator' : 'Sign in to enable private simulator channels'}</h2>
            <p className="page--ubi-sim__description">
              {isConfigured
                ? 'Use the account menu to log in, then keep this endpoint open for directed simulator traffic.'
                : 'Supabase auth is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY to enable authenticated simulator sessions.'}
            </p>
          </div>
          <div className={`page--ubi-sim__status ${isAuthenticated ? 'authenticated' : ''}`}>
            <span className="page--ubi-sim__status-dot" />
            <span>{isAuthenticated ? 'Authenticated' : 'Guest session'}</span>
          </div>
        </div>
        <input
          className="page--ubi-sim__label"
          type="text"
          placeholder="Enter node name"
          value={simName}
          onChange={(e) => { setSimName(e.target.value) }}
          disabled={!isAuthenticated}
        />
        <div className="page--ubi-sim__intensity-indicator">
          <p>Intensity: {intensity}</p>
          <div className="page--ubi-sim__intensity-indicator__dot" style={{ opacity: 0.9 * (intensity / 128) + 0.1 }}></div>
        </div>
        <div className="page--ubi-sim__is-playing">
          <p>isPlaying:</p>
          <div className={`page--ubi-sim__is-playing__dot ${isPlaying ? 'playing' : ''}`}></div>
        </div>
        <div className="page--ubi-sim__is-playing">
          <p>isMuted:</p>
          <div className={`page--ubi-sim__is-playing__dot ${isMuted ? 'playing' : ''}`}></div>
        </div>
        <div className="page--ubi-sim__device-emulation">
          <label>
            <input
              type="checkbox"
              checked={emulateDevice}
              onChange={(e) => {
                setEmulateDevice(e.target.checked);
                if (e.target.checked) setRevokedNotice(false);
              }}
              disabled={!isAuthenticated}
            />
            Emulate ESP32 device (track presence as <code>kind: device</code>)
          </label>
          <input
            className="page--ubi-sim__label"
            type="text"
            placeholder="hardware_id"
            value={hardwareId}
            onChange={(e) => setHardwareId(e.target.value)}
            disabled={!isAuthenticated || !emulateDevice}
          />
          <p className="page--ubi-sim__description">
            Enter the <code>hardware_id</code> of a claimed device row to flip it
            Online on the Devices page, or keep the generated id to appear as an
            unclaimed device on the dashboard's Device node.
          </p>
          {revokedNotice && (
            <p className="page--ubi-sim__description">
              Device identity revoked from the Devices page — emulation stopped
              (a real ESP32 wipes its EEPROM and re-enters pairing here).
            </p>
          )}
        </div>
        <div className="page--ubi-sim__session-meta">
          <p>Connection ID: <code>{realtimeClient.getConnectionId() || 'connecting...'}</code></p>
          <p>Channel: <code>{realtimeClient.getChannelName() || 'disconnected'}</code></p>
          <p>User: <code>{user?.id || 'guest'}</code></p>
        </div>
        <button className="page--ubi-sim__button" onClick={() => { emitBufferUpdatedHandler() }} disabled={!isAuthenticated}>
          Send <code>ack.bufferUpdated</code>
        </button>
      </div>
    </div>
  )
}

export default UbiSim;
